"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Users, Search, Compass, TrendingUp, AlertCircle, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
export default function WhatIsB2bSeo() {
  const shouldReduceMotion = useReducedMotion();
  const comparisonRows = [{
    category: "Buyers",
    icon: Users,
    b2b: "Multiple stakeholders across evaluation: procurement leads, technical evaluators, finance teams and senior sponsors.",
    consumer: "Individual shoppers making single-decision purchases."
  }, {
    category: "Search intent",
    icon: Search,
    b2b: "Commercial and supplier-evaluation queries. Keyword research prioritises commercial intent over raw search volume.",
    consumer: "Broad, high-volume and emotion-driven searches."
  }, {
    category: "Evaluation",
    icon: Compass,
    b2b: "Longer evaluation cycles with complex information needs, governance questions and risk mitigation.",
    consumer: "Quicker decisions with shorter research phases."
  }, {
    category: "Success measure",
    icon: TrendingUp,
    b2b: "Qualified enquiries, lead quality, sales opportunities and pipeline contribution.",
    consumer: "Raw visit numbers, instant checkouts and consumer transactions."
  }];
  const commonProblems = ["Low visibility for high-intent non-branded commercial searches that match your core capabilities.", "Organic traffic arriving from the wrong audience, including casual researchers, students and job seekers.", "Weak service and product pages that fail to answer key evaluation, delivery and procurement questions.", "Competitors dominating critical comparison, alternative and shortlist search queries.", "Blog and informational content that attracts page views but delivers few qualified enquiries.", "Messy, siloed website architecture that buries your highest-value commercial landing pages.", "Attribution gaps between CRM sales enquiries and the organic search touchpoints that introduced them."];
  return <section id="whatisb2bseo" data-nav="light" className="bg-muted/40 text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            Specialist Strategic Focus
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            What a B2B SEO consultant does
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            B2B SEO is search optimisation designed for how businesses research, compare and
            shortlist suppliers, not for impulse consumer purchases. As a B2B SEO consultant, I align
            organic search strategy with commercial goals: diagnosing content, technical and conversion
            issues, planning a keyword and content roadmap around genuine buyer intent, guiding
            implementation, and measuring success against qualified enquiries and pipeline contribution
            rather than just traffic numbers.
          </p>
        </div>

        {/* Comparison Grid: B2B vs Consumer SEO */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} className="mb-16">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-6 pb-3 border-b border-border">
            <div>
              <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
                B2B SEO vs consumer SEO
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Key structural differences in how organic search functions for business-to-business
              </p>
            </div>
            <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground font-medium">
              Side-by-side comparison
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {/* B2B Column */}
            <Card className="border-2 border-primary/30 bg-card shadow-xs">
              <CardHeader className="pb-4 border-b border-border/80 bg-primary/5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg sm:text-xl font-semibold text-primary font-default">
                    B2B Search Optimisation
                  </CardTitle>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded bg-primary text-primary-foreground">
                    High intent
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Structured around buying committees, evaluation depth and commercial outcomes
                </p>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {comparisonRows.map((row, idx) => {
                const Icon = row.icon;
                return <div key={`b2b-row-${idx}`} className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary">
                        <Icon className="size-4 shrink-0" />
                        <span>{row.category}</span>
                      </div>
                      <p className="text-sm text-foreground leading-relaxed pl-6">
                        {row.b2b}
                      </p>
                    </div>;
              })}
              </CardContent>
            </Card>

            {/* Consumer Column */}
            <Card className="border border-border bg-card/60 shadow-xs">
              <CardHeader className="pb-4 border-b border-border/80 bg-muted/30">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg sm:text-xl font-semibold text-foreground/80 font-default">
                    Consumer SEO (B2C)
                  </CardTitle>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-muted text-muted-foreground">
                    High volume
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Structured around rapid checkouts, brand awareness and broad interest
                </p>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {comparisonRows.map((row, idx) => {
                const Icon = row.icon;
                return <div key={`consumer-row-${idx}`} className="space-y-1.5">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <Icon className="size-4 shrink-0" />
                        <span>{row.category}</span>
                      </div>
                      <p className="text-sm text-muted-foreground leading-relaxed pl-6">
                        {row.consumer}
                      </p>
                    </div>;
              })}
              </CardContent>
            </Card>
          </div>

          {/* Illustrative example contrast */}
          <div className="mt-6 p-4 rounded-lg bg-card border border-border text-xs sm:text-sm text-muted-foreground flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <span className="font-semibold text-foreground">Intent contrast example: </span>
              <span className="text-primary font-medium">"industrial air compressor maintenance contract"</span>
              {" "}(commercial buyer evaluating suppliers) versus{" "}
              <span className="italic">"cheap compressor for home"</span>
              {" "}(consumer retail query).
            </div>
            <span className="text-xs text-muted-foreground/80 shrink-0 uppercase tracking-wider font-mono">
              illustrative example, not client data
            </span>
          </div>
        </motion.div>

        {/* Why B2B Companies Need a Specialist Strategy */}
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="bg-card border border-border rounded-2xl p-6 sm:p-10 shadow-sm">
          <div className="max-w-3xl mb-8">
            <h3 className="text-xl sm:text-2xl font-semibold font-default text-foreground">
              Why B2B companies need a specialist strategy
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Applying generic consumer-style tactics to complex B2B services leaves commercial revenue on
              the table. Here are the core search and lead generation problems I identify most often during initial audits:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {commonProblems.map((problem, i) => <div key={`problem-${i}`} className="flex items-start gap-3 p-3.5 rounded-lg border border-border/70 bg-background/60">
                <div className="size-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5">
                  <AlertCircle className="size-3.5" />
                </div>
                <p className="text-sm text-foreground leading-relaxed">{problem}</p>
              </div>)}
          </div>

          {/* Closing action card */}
          <div className="mt-10 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <p className="text-base font-semibold text-foreground">
                Ready to align your organic search with your sales pipeline?
              </p>
              <p className="text-sm text-muted-foreground mt-0.5">
                Discuss your business model, target accounts and commercial search visibility directly with me.
              </p>
            </div>
            <Button asChild size="lg" className="w-full sm:w-auto rounded-full font-medium shrink-0">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}